import { Divider, Typography } from "@suraasa/placebo-ui"
import { useQuery } from "@tanstack/react-query"
import api from "api"
import { queries } from "api/queries"
import { LearningItem } from "api/resources/learningItems/types"
import clsx from "clsx"
import Fallback from "components/Fallback"
import { useCourseDetails } from "features/LearningItems/Course"
import React, { useState } from "react"
import { useParams } from "react-router-dom"
import { useScrollToField } from "utils/hooks/useScrollToField"

import ClassDetailsCard from "./LiveClassCard"
import { RecordedClassCard } from "./RecordedClassesList"
import VideoPlayerDialog from "./VideoPlayerDialog"

type Tab = "live" | "recorded"

/**
 * Courses don't have a dedicated classes tab in the new delivery mode.
 * `CourseClassesRoute` redirects `/<itemType>/<itemSlug>/classes` to the overview
 * page with this section highlighted, so the learner lands on the classes list.
 */
export const CLASSES_SECTION_ID = "live-classes"

type Props = {
  /** Defaults to none — the standalone `/course/:slug/classes` route has no
   * recorded classes data available, so it only ever shows live classes. */
  recordedClasses?: LearningItem[]
  /** Defaults to true, matching the standalone route's prior behavior. */
  showLive?: boolean
  hideIfNoData?: boolean
  heading?: React.ReactNode
}

const Classes = ({
  recordedClasses = [],
  showLive = true,
  hideIfNoData,
  heading,
}: Props) => {
  const [recordingURL, setRecordingURL] = useState<string | null>(null)
  const [selectedTab, setSelectedTab] = useState<Tab>("live")

  const { elementId } = useScrollToField(CLASSES_SECTION_ID)

  const { course } = useCourseDetails()
  const { slug } = useParams() as { slug: string }
  const plannerId = course.id

  const liveClasses = useQuery({
    enabled: showLive && !!course.id,
    queryKey: queries.learningItems.listClasses(course.type, course.id)
      .queryKey,
    queryFn: () =>
      api.learningItems.classes.list({
        urlParams: {
          learningItemType: course.type,
          learningItemId: course.id,
        },
      }),
  })

  const onViewRecording = (recordingUrl: string) => {
    setRecordingURL(recordingUrl)
  }
  const onClose = () => {
    setRecordingURL(null)
  }

  const hasRecorded = recordedClasses.length > 0
  const hasLive =
    showLive && liveClasses.isSuccess && liveClasses.data.length > 0
  const liveStillLoading = showLive && liveClasses.isLoading

  const showTabBar = hasLive && hasRecorded

  // Until live classes settle, always default to the "live" tab (showing its
  // loading/error state) rather than optimistically showing recorded classes
  // — that would swap real content out from under the user the moment live
  // classes resolve and the tab bar appears, and would silently hide a live
  // classes error behind an unrelated "no recorded classes" empty state.
  // Only fall back to "recorded" when there actually is recorded data —
  // otherwise (e.g. the standalone live-only route) stay on "live" so its own
  // empty/error state renders instead of an unrelated recorded-classes one.
  const activeTab: Tab = showTabBar
    ? selectedTab
    : hasRecorded && !hasLive && !liveStillLoading
    ? "recorded"
    : "live"

  const nothingToShow =
    !hasRecorded && (!showLive || (liveClasses.isSuccess && !hasLive))

  if (hideIfNoData && nothingToShow) return null

  return (
    <div className="relative" id={elementId || undefined}>
      {heading || (
        <Typography variant="title3" className="mb-2">
          Classes
        </Typography>
      )}

      {showTabBar && (
        <div role="tablist" className="mb-2 flex gap-3">
          {(["live", "recorded"] as Tab[]).map(tab => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={tab === activeTab}
              onClick={() => setSelectedTab(tab)}
              className={clsx("-mb-px border-b-2 px-0.25 pb-1", {
                "border-primary-500 text-primary-500": tab === activeTab,
                "border-transparent text-muted": tab !== activeTab,
              })}
            >
              <Typography
                variant={tab === activeTab ? "strong" : "body"}
                className="capitalize"
              >
                {tab}
              </Typography>
            </button>
          ))}
        </div>
      )}

      <VideoPlayerDialog recordingURL={recordingURL} onClose={onClose} />

      {activeTab === "live" && (
        <>
          {(liveClasses.isLoading || liveClasses.isError) && (
            <Fallback
              isLoading={liveClasses.isLoading}
              hasError={liveClasses.isError}
            />
          )}

          {liveClasses.isSuccess &&
            (hasLive ? (
              <div className="flex flex-col rounded-2xl border border-onSurface-200 bg-white">
                {liveClasses.data.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <ClassDetailsCard
                      data={item}
                      onViewRecording={onViewRecording}
                      flat
                    />
                    {index !== liveClasses.data.length - 1 && (
                      <Divider className="bg-onSurface-200" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <Fallback
                className="mt-5"
                hasError={false}
                data={{ title: "No live classes to show" }}
              />
            ))}
        </>
      )}

      {activeTab === "recorded" &&
        (hasRecorded ? (
          <div className="flex flex-col rounded-2xl border border-onSurface-200 bg-white">
            {recordedClasses.map((item, index) => (
              <React.Fragment key={item.id}>
                <RecordedClassCard
                  data={item}
                  slug={slug}
                  plannerId={plannerId}
                  flat
                />
                {index !== recordedClasses.length - 1 && (
                  <Divider className="bg-onSurface-200" />
                )}
              </React.Fragment>
            ))}
          </div>
        ) : (
          <Fallback
            className="mt-5"
            hasError={false}
            data={{ title: "No recorded classes to show" }}
          />
        ))}
    </div>
  )
}

export default Classes
